import { useEffect, useState } from "react";

/**
 * Vanilla `Plaid.create({...})` wrapper. The official `react-plaid-link`
 * package targets React ≤18; Hearth ships React 19. Inject the script
 * once at hook-mount time and call `Plaid.create({ token, onSuccess })`
 * directly.
 *
 * The hook returns `open(token)` which:
 *   1. Lazy-loads the Plaid Link JS once (idempotent across calls).
 *   2. Initializes a handler with the supplied link_token.
 *   3. Opens the modal.
 *   4. Calls onSuccess(public_token, metadata) when the user finishes.
 */

interface PlaidMetadata {
  institution?: { name?: string; institution_id?: string };
}

interface PlaidHandler {
  open(): void;
  exit(): void;
  destroy(): void;
}

interface PlaidGlobal {
  create(args: {
    token: string;
    onSuccess: (publicToken: string, metadata: PlaidMetadata) => void;
    onExit?: (error: unknown, metadata: PlaidMetadata) => void;
    onLoad?: () => void;
    onEvent?: (eventName: string, metadata: PlaidMetadata) => void;
  }): PlaidHandler;
}

declare global {
  interface Window {
    Plaid?: PlaidGlobal;
  }
}

const PLAID_LINK_URL = "https://cdn.plaid.com/link/v2/stable/link-initialize.js";
let scriptPromise: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    if (typeof window === "undefined") {
      reject(new Error("Plaid Link requires a browser environment"));
      return;
    }
    if (window.Plaid) {
      resolve();
      return;
    }
    const tag = document.createElement("script");
    tag.src = PLAID_LINK_URL;
    tag.async = true;
    tag.onload = () => resolve();
    tag.onerror = () => reject(new Error("Failed to load Plaid Link script"));
    document.head.appendChild(tag);
  });
  return scriptPromise;
}

export interface UsePlaidLinkArgs {
  onSuccess: (
    publicToken: string,
    institutionName: string,
    institutionId?: string,
  ) => void;
  onExit?: () => void;
}

export function usePlaidLink({ onSuccess, onExit }: UsePlaidLinkArgs) {
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    loadScript().catch((e) => setError((e as Error).message));
  }, []);

  async function open(linkToken: string) {
    setError(null);
    try {
      await loadScript();
    } catch (e) {
      setError((e as Error).message);
      return;
    }
    if (!window.Plaid) {
      setError("Plaid Link script loaded but window.Plaid is undefined");
      return;
    }
    const handler = window.Plaid.create({
      token: linkToken,
      onSuccess: (publicToken, metadata) => {
        const inst = metadata.institution;
        onSuccess(publicToken, inst?.name ?? "Unknown", inst?.institution_id);
      },
      onExit: () => onExit?.(),
    });
    handler.open();
  }

  return { open, error } as const;
}
